import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { useAuth } from "../utils/auth";
import { useUser } from "../contexts/UserContext";
import { submitContactForm, postEmailForVerification } from "../utils/api";
import { UIUtils } from "../utils/uiUtils";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import config from "../configs/config";
import PageMetadata from "../components/PageMetadata";
import { FiDownload } from "react-icons/fi";
import lightBulbIcon from "../assets/icons/light-bulb-icon.svg";
import { URL_PATHS } from "../utils/routeUtils";

// Define the form data interface
interface ContactFormData {
  whoYouAre: "user" | "not_user" | "";
  helpWith:
    | "application"
    | "technical"
    | "complaint"
    | "other"
    | "delete_account"
    | "";
  name: string;
  email: string;
  confirmEmail: string;
  message: string;
}

// Define the error interface with string error messages
interface FormErrors {
  [key: string]: string | undefined;
}

const ContactUs: React.FC = () => {
  const { user } = useAuth();
  const { user: userData } = useUser();
  const navigate = useNavigate();
  const { setIsVisible } = useBottomNavBar();

  useEffect(() => {
    setIsVisible(false);
    if (user) {
      setIsVisible(true);
    }
    // return () => setIsVisible(false);
  }, [setIsVisible, user]);
  // Form state
  const [formData, setFormData] = useState<ContactFormData>({
    whoYouAre: "",
    helpWith: "",
    name: "",
    email: "",
    confirmEmail: "",
    message: "",
  });

  // Form validation errors
  const [errors, setErrors] = useState<FormErrors>({});

  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Email login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginEmailError, setLoginEmailError] = useState("");
  const [isEmailVerificationSent, setIsEmailVerificationSent] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);

  // Prefill form data if user is logged in
  useEffect(() => {
    if (user && userData) {
      setFormData((prev) => ({
        ...prev,
        name: userData.first_name
          ? `${userData.first_name} ${userData.last_name || ""}`.trim()
          : "",
        email: userData.email || "",
        confirmEmail: userData.email || "",
      }));
    }
  }, [user, userData]);

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when field is edited
    if (errors[name as keyof ContactFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // Handle radio button selection for "Who you are"
  const handleWhoYouAreChange = (value: "user" | "not_user") => {
    setFormData((prev) => ({
      ...prev,
      whoYouAre: value,
    }));

    // Clear error
    if (errors.whoYouAre) {
      setErrors((prev) => ({
        ...prev,
        whoYouAre: undefined,
      }));
    }
  };

  // Handle radio button selection for "What can we help you with"
  const handleHelpWithChange = (
    value:
      | "application"
      | "technical"
      | "complaint"
      | "other"
      | "delete_account",
  ) => {
    setFormData((prev) => ({
      ...prev,
      helpWith: value,
    }));

    // Clear error
    if (errors.helpWith) {
      setErrors((prev) => ({
        ...prev,
        helpWith: undefined,
      }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // If not logged in, validate "Who you are"
    if (!user && !formData.whoYouAre) {
      newErrors.whoYouAre = "Please select who you are";
    }

    // If not logged in and selected "not_user", validate other fields
    if (!user && formData.whoYouAre === "not_user") {
      if (!formData.helpWith) {
        newErrors.helpWith = "Please select what we can help you with";
      }

      if (!formData.name.trim()) {
        newErrors.name = "Please enter your name";
      }

      if (!formData.email.trim()) {
        newErrors.email = "Please enter your email";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }

      if (!formData.confirmEmail.trim()) {
        newErrors.confirmEmail = "Please confirm your email";
      } else if (formData.email !== formData.confirmEmail) {
        newErrors.confirmEmail = "Emails do not match";
      }
    }

    // If logged in, validate required fields
    if (user) {
      if (!formData.helpWith) {
        newErrors.helpWith = "Please select what we can help you with";
      }

      if (!formData.name.trim()) {
        newErrors.name = "Please enter your name";
      }
    }

    // Message is required for all users EXCEPT when deleting account
    if (formData.helpWith !== "delete_account" && !formData.message.trim()) {
      newErrors.message = "Please enter your message";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // If delete account is selected, navigate to preference center
    if (formData.helpWith === "delete_account") {
      navigate(URL_PATHS.PREFERENCES);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      // Regular contact form submission
      // Prepare contact data
      const contactData = {
        whoYouAre: user ? "user" : formData.whoYouAre, // For logged in users, always set as 'user'
        helpWith: formData.helpWith,
        name: formData.name,
        email: formData.email,
        message: formData.message,
      };

      // Log the form data being sent to the API
      console.log("Form data to be sent:", contactData);

      // Submit the contact form data to the backend
      await submitContactForm(contactData);

      // Track form submission
      UIUtils.trackButtonClick("Contact Form Submit");

      setSubmitSuccess(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitError(
        "There was an error processing your request. Please try again later.",
      );
      UIUtils.trackException("Form submission", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle email login input change
  const handleLoginEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLoginEmail(value);

    // Clear error when field is edited
    if (loginEmailError) {
      setLoginEmailError("");
    }
  };

  // Validate login email
  const validateLoginEmail = (): boolean => {
    if (!loginEmail.trim()) {
      setLoginEmailError("Please enter your email address");
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
      setLoginEmailError("Please enter a valid email address");
      return false;
    }

    return true;
  };

  // Handle email verification submission
  const handleSendVerificationCode = async () => {
    if (!validateLoginEmail()) {
      return;
    }

    setIsSendingVerification(true);
    setLoginEmailError("");

    try {
      console.log("Sending verification code for email:", loginEmail);
      const response = await postEmailForVerification(loginEmail, "contact_us");

      console.log("Email verification response:", response);

      if (response.data && response.data.reference_id) {
        // Track email verification request
        UIUtils.trackButtonClick("Contact Form Email Verification");

        // Navigate to email verification page with redirect state
        navigate(
          `/email-verification/${response.data.reference_id}?intent=contact_us`,
          {
            state: {
              from: "/contact-us",
              fromContactForm: true,
            },
          },
        );
      } else {
        console.error("Invalid response structure:", response.data);
        setLoginEmailError("An error occurred. Please try again.");
      }
    } catch (error: any) {
      console.error("Error sending verification code:", error);
      console.error("Error response:", error.response?.data);

      // More specific error handling
      if (error.response?.status === 400) {
        setLoginEmailError("Invalid email address or email not found.");
      } else if (error.response?.status === 429) {
        setLoginEmailError(
          "Too many requests. Please wait a moment and try again.",
        );
      } else if (error.response?.data?.message) {
        setLoginEmailError(error.response.data.message);
      } else {
        setLoginEmailError("An error occurred. Please try again.");
      }

      UIUtils.trackException("Contact Form Email Verification", error);
    } finally {
      setIsSendingVerification(false);
    }
  };

  // Reset email login state when radio button changes
  useEffect(() => {
    if (formData.whoYouAre !== "user") {
      setLoginEmail("");
      setLoginEmailError("");
      setIsEmailVerificationSent(false);
      setIsSendingVerification(false);
    }
  }, [formData.whoYouAre]);

  // Handle back to home navigation
  const handleBackToHome = () => {
    if (user) {
      navigate("/customer-home");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="content-container">
      <div className="text-center mb-6">
        <h1 className="text-4xl">Contact us</h1>
        <p className="font-semibold mt-4">
          We typically reply within 2 working days, and often much sooner.
        </p>
      </div>
      <div className="container-white-rounded">
        <PageMetadata
          title="Afforda | Contact us"
          description="Contact us to help us assist you"
        />

        {/* Show success message and hide form */}
        {submitSuccess && (
          <div className="text-center space-y-6">
            <div className="bg-success-lighter border border-success-light rounded-8 p-6">
              <h3 className="text-lg font-semibold text-success-dark mb-2">
                Message Sent Successfully!
              </h3>
              <p className="text-success-dark">
                Thank you for your message, we will get back to you as soon as
                possible.
              </p>
            </div>
            <Button
              text="Back to Home"
              onClick={handleBackToHome}
              className="w-full md:w-auto"
            />
          </div>
        )}

        {/* Show error message and hide form */}
        {submitError && (
          <div className="text-center space-y-6">
            <div className="bg-error-lighter border border-error-light rounded-8 p-6">
              <h2 className="text-lg font-semibold text-error-dark mb-2">
                Error Sending Message
              </h2>
              <p className="text-error-dark">{submitError}</p>
            </div>
            <Button
              text="Back to Home"
              onClick={handleBackToHome}
              className="w-full md:w-auto"
            />
          </div>
        )}

        {/* Show form only if no success or error messages */}
        {!submitSuccess && !submitError && (
          <form onSubmit={handleSubmit} className="space-y-6 contact-form">
            {/* Who you are - only show if not logged in */}
            {!user && (
              <div>
                <h2 className="form-label">Have you used Afforda before?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div
                    className={`radio-button ${
                      formData.whoYouAre === "user"
                        ? "bg-grey-20"
                        : "hover:bg-grey-20"
                    }`}
                    onClick={() => handleWhoYouAreChange("user")}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="user"
                        name="whoYouAre"
                        value="user"
                        checked={formData.whoYouAre === "user"}
                        onChange={() => handleWhoYouAreChange("user")}
                        className="mr-2"
                      />
                      <label htmlFor="user">
                        Yes, I already have an account
                      </label>
                    </div>
                  </div>

                  <div
                    className={`radio-button ${
                      formData.whoYouAre === "not_user"
                        ? "bg-grey-20"
                        : "hover:bg-grey-20"
                    }`}
                    onClick={() => handleWhoYouAreChange("not_user")}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="not_user"
                        name="whoYouAre"
                        value="not_user"
                        checked={formData.whoYouAre === "not_user"}
                        onChange={() => handleWhoYouAreChange("not_user")}
                        className="mr-2"
                      />
                      <label htmlFor="not_user">No, I'm new here</label>
                    </div>
                  </div>
                </div>
                {errors.whoYouAre && (
                  <p className="text-red text-sm mt-1">{errors.whoYouAre}</p>
                )}
              </div>
            )}

            {/* Show email login form if user selected */}
            {!user && formData.whoYouAre === "user" && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="loginEmail" className="form-label">
                    Welcome back! Let’s get you signed in
                  </label>
                  <input
                    type="email"
                    id="loginEmail"
                    name="loginEmail"
                    value={loginEmail}
                    onChange={handleLoginEmailChange}
                    placeholder="Email*"
                    aria-label="Email"
                    className={`form-input-secondary ${
                      loginEmailError ? "border-red" : ""
                    }`}
                  />
                  {loginEmailError && (
                    <p className="text-red text-sm mt-1">{loginEmailError}</p>
                  )}
                </div>

                <div>
                  <Button
                    text="Login"
                    onClick={handleSendVerificationCode}
                    isLoading={isSendingVerification}
                    loadingText="Sending..."
                    disabled={isSendingVerification}
                    className="w-full"
                  />
                </div>

                {isEmailVerificationSent && (
                  <div className="bg-success-lighter border border-success-light rounded-8 p-4">
                    <p className="text-success-dark">
                      Verification code sent! Check your email and click the
                      link to continue.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Show form if not_user selected or user is logged in */}
            {(user || formData.whoYouAre === "not_user") && (
              <>
                {/* What can we help you with */}
                <div>
                  <h2 className="form-label">
                    What can we help you with today?
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div
                      className={`radio-button ${
                        formData.helpWith === "application"
                          ? "bg-grey-20"
                          : "hover:bg-grey-20"
                      }`}
                      onClick={() => handleHelpWithChange("application")}
                    >
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="application"
                          name="helpWith"
                          value="application"
                          checked={formData.helpWith === "application"}
                          onChange={() => handleHelpWithChange("application")}
                          className="mr-2"
                        />
                        <label htmlFor="application">
                          Complete an application
                        </label>
                      </div>
                    </div>

                    <div
                      className={`radio-button ${
                        formData.helpWith === "technical"
                          ? "bg-grey-20"
                          : "hover:bg-grey-20"
                      }`}
                      onClick={() => handleHelpWithChange("technical")}
                    >
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="technical"
                          name="helpWith"
                          value="technical"
                          checked={formData.helpWith === "technical"}
                          onChange={() => handleHelpWithChange("technical")}
                          className="mr-2"
                        />
                        <label htmlFor="technical">Technical issues</label>
                      </div>
                    </div>

                    <div
                      className={`radio-button ${
                        formData.helpWith === "complaint"
                          ? "bg-grey-20"
                          : "hover:bg-grey-20"
                      }`}
                      onClick={() => handleHelpWithChange("complaint")}
                    >
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="complaint"
                          name="helpWith"
                          value="complaint"
                          checked={formData.helpWith === "complaint"}
                          onChange={() => handleHelpWithChange("complaint")}
                          className="mr-2"
                        />
                        <label htmlFor="complaint">Make a complaint</label>
                      </div>
                    </div>

                    {/* Delete account option - only for logged-in users */}
                    {user && (
                      <div
                        className={`radio-button ${
                          formData.helpWith === "delete_account"
                            ? "bg-grey-20"
                            : "hover:bg-grey-20"
                        }`}
                        onClick={() => handleHelpWithChange("delete_account")}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="delete_account"
                            name="helpWith"
                            value="delete_account"
                            checked={formData.helpWith === "delete_account"}
                            onChange={() =>
                              handleHelpWithChange("delete_account")
                            }
                            className="mr-2"
                          />
                          <label htmlFor="delete_account">
                            Delete my account
                          </label>
                        </div>
                      </div>
                    )}

                    <div
                      className={`radio-button ${
                        formData.helpWith === "other"
                          ? "bg-grey-20"
                          : "hover:bg-grey-20"
                      }`}
                      onClick={() => handleHelpWithChange("other")}
                    >
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="other"
                          name="helpWith"
                          value="other"
                          checked={formData.helpWith === "other"}
                          onChange={() => handleHelpWithChange("other")}
                          className="mr-2"
                        />
                        <label htmlFor="other">Something else</label>
                      </div>
                    </div>
                  </div>
                  {errors.helpWith && (
                    <p className="text-red text-sm mt-1">{errors.helpWith}</p>
                  )}
                </div>

                {/* Info box for delete account */}
                {formData.helpWith === "delete_account" && (
                  <div className="border-2 border-primary rounded-10 p-4 mb-4 flex items-start gap-3">
                    <img
                      src={lightBulbIcon}
                      alt="Info"
                      className="w-10 h-10 flex-shrink-0 mt-1"
                    />
                    <div className="text-sm md:text-base">
                      <p>
                        Did you know that Afforda is not a paid subscription
                        service?
                      </p>
                      <p>It is 100% free to use.</p>
                      <p className="mt-4">
                        Still want to delete your account? Click Continue. You
                        can delete it on the next page.
                      </p>
                    </div>
                  </div>
                )}

                {/* Alert box for complaints */}
                {formData.helpWith === "complaint" && (
                  <div
                    className="border-2 border-primary rounded-10 p-4 mb-4 flex items-center cursor-pointer hover:bg-grey-20 transition-colors"
                    onClick={() =>
                      window.open(
                        `${config.AFFORDA_STATIC_SITE}/documents/Afforda-Complaints-Handling-Procedure.pdf`,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    <FiDownload className="w-5 h-5 flex-shrink-0" />
                    <p className="ml-2">
                      Click here to download our full complaints handling
                      procedure.
                    </p>
                  </div>
                )}

                {/* Name */}
                <div>
                  <h2 className="form-label">Enter your details</h2>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Name*"
                    aria-label="Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`form-input-secondary ${
                      errors.name ? "border-red" : ""
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email - if logged in, show as disabled */}
                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Email*"
                    aria-label="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!!user}
                    className={`form-input-secondary ${
                      user
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : "form-input-secondary"
                    } ${errors.email ? "border-red" : ""}`}
                  />
                  {errors.email && (
                    <p className="text-red text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Confirm Email - only show if not logged in */}
                {!user && (
                  <div>
                    <input
                      type="email"
                      id="confirmEmail"
                      name="confirmEmail"
                      placeholder="Confirm email*"
                      aria-label="Confirm email"
                      value={formData.confirmEmail}
                      onChange={handleInputChange}
                      className={`form-input-secondary ${
                        errors.confirmEmail ? "border-red" : ""
                      }`}
                    />
                    {errors.confirmEmail && (
                      <p className="text-red text-sm mt-1">
                        {errors.confirmEmail}
                      </p>
                    )}
                  </div>
                )}

                {/* Message - hide for delete account */}
                {formData.helpWith !== "delete_account" && (
                  <div>
                    <textarea
                      id="message"
                      name="message"
                      placeholder="Add a message*"
                      aria-label="Add a message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className={`form-input-secondary ${
                        errors.message ? "border-red" : ""
                      }`}
                    />
                    {errors.message && (
                      <p className="text-red text-sm mt-1">{errors.message}</p>
                    )}
                  </div>
                )}

                {/* Submit button */}
                <div>
                  {formData.helpWith !== "delete_account" && (
                    <p className="text-sm mb-2 mx-2">
                      By submitting this form, you agree that we may process
                      your data in accordance with our{" "}
                      <a
                        href={`${config.AFFORDA_STATIC_SITE}/privacy-policy`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Privacy Policy
                      </a>
                    </p>
                  )}
                  <Button
                    text={
                      formData.helpWith === "delete_account"
                        ? "Continue"
                        : "Submit"
                    }
                    type="submit"
                    onClick={() => {}}
                    isLoading={isSubmitting}
                    loadingText="Sending..."
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactUs;
